import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { RoleContext } from 'src/context/roleContext';
import { API_BASE_URL } from 'src/apiConfig';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import {
  FLAG_FINALE,
  FLAG_REZULTATI,
  FLAG_ZALBE,
  modeDescriptions
} from 'src/constants/competitions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* strana za skolu gde vidi rezultate i statisktu rezultata */

const StatisticsByClass = () => {
  const [data, setData] = useState([]);
  const { t } = useTranslation();
  const { schoolId } = useContext(RoleContext);
  const [viewMode, setViewMode] = useState(false);

  const { activeModes } = useContext(ActiveModesContext);
  const activeModeKey = activeModes.find((mode) => modeDescriptions[mode]);

  const activeMode = activeModeKey
    ? t(`modes.${activeModeKey}`, { defaultValue: activeModeKey })
    : t('noActiveMode');

  useEffect(() => {
    setViewMode(
      activeModes.includes(FLAG_REZULTATI) ||
        activeModes.includes(FLAG_ZALBE) ||
        activeModes.includes(FLAG_FINALE)
    );
  }, [activeModes]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/statistics.php?statistics=statisticsByClass&school_id=${schoolId}`
        );
        if (response.data) {
          setData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const barData = {
    labels: data.map((item) => `${t('statistics.grade')} ${item.Razred}`),
    datasets: [
      {
        label: t('statistics.numberOfStudents'),
        data: data.map((item) => item['Broj Ucenika']),
        backgroundColor: '#36A2EB',
        borderColor: '#36A2EB',
        borderWidth: 1,
        hoverBackgroundColor: '#1976d2'
      }
    ]
  };

  const lineData = {
    labels: data.map((item) => `${t('statistics.grade')} ${item.Razred}`),
    datasets: [
      {
        label: t('statistics.averageScore'),
        data: data.map((item) => item['Prosecan Rezultat']),
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
        tension: 0.4
      },
      {
        label: t('statistics.highestScore'),
        data: data.map((item) => item['Najvisi Rezultat']),
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const renderTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: '16px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Table>
        <TableHead sx={{ bgcolor: '#1976d2' }}>
          <TableRow>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.grade')}
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.numberOfStudents')}
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.averageScore')}
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.lowestScore')}
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.highestScore')}
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('statistics.totalPoints')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                bgcolor: index % 2 === 0 ? '#f9f9f9' : 'white',
                '&:hover': { bgcolor: '#f1f1f1' }
              }}
            >
              <TableCell>{row.Razred}</TableCell>
              <TableCell>{row['Broj Ucenika']}</TableCell>
              <TableCell>{row['Prosecan Rezultat']}</TableCell>
              <TableCell>{row['Najnizi Rezultat']}</TableCell>
              <TableCell>{row['Najvisi Rezultat']}</TableCell>
              <TableCell>{row['Ukupno Poena']}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!viewMode) {
    return (
      <Box
        display="grid"
        gap={3}
        p={2}
        sx={{
          backgroundColor: '#e0f7fa',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" color="textSecondary">
          {t('statistics.noViewStatisticsCondition')} - <b>{activeMode}</b>
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography
        variant="h4"
        sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold' }}
      >
        {t('statistics.resultsByGrade')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {renderTable()}
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={4}
            sx={{ p: 3, borderRadius: '16px', backgroundColor: '#ffffff' }}
          >
            <Bar data={barData} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={4}
            sx={{ p: 3, borderRadius: '16px', backgroundColor: '#ffffff' }}
          >
            <Line data={lineData} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatisticsByClass;
